/**
 * SnakeGame - Mini jeu Snake pendant le chargement
 * Parce que c'est plus fun qu'un spinner
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Gamepad2, Trophy, RotateCcw } from 'lucide-react'

const GRID_SIZE = 12
const CELL_SIZE = 14
const INITIAL_SPEED = 150

const DIRECTIONS = {
  UP: { x: 0, y: -1 },
  DOWN: { x: 0, y: 1 },
  LEFT: { x: -1, y: 0 },
  RIGHT: { x: 1, y: 0 },
}

export default function SnakeGame({ color = 'primary' }) {
  const [snake, setSnake] = useState([{ x: 6, y: 6 }])
  const [food, setFood] = useState({ x: 3, y: 3 })
  const [direction, setDirection] = useState(DIRECTIONS.RIGHT)
  const [gameOver, setGameOver] = useState(false)
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('emd_snake_highscore') || '0')
  })
  const [isPaused, setIsPaused] = useState(false)
  const [showControls, setShowControls] = useState(true)

  const directionRef = useRef(direction)
  const gameLoopRef = useRef(null)
  const containerRef = useRef(null)

  // Color variants
  const colors = {
    primary: {
      head: 'bg-primary-400',
      body: 'bg-primary-500/70',
      food: 'bg-accent-400',
      border: 'border-primary-500/30',
      text: 'text-primary-400',
    },
    emerald: {
      head: 'bg-emerald-400',
      body: 'bg-emerald-500/70',
      food: 'bg-amber-400',
      border: 'border-emerald-500/30',
      text: 'text-emerald-400',
    },
  }[color] || colors.primary

  // Generate random food position
  const generateFood = useCallback((currentSnake) => {
    let newFood
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE),
      }
    } while (currentSnake.some(seg => seg.x === newFood.x && seg.y === newFood.y))
    return newFood
  }, [])

  // Reset game
  const resetGame = useCallback(() => {
    const initialSnake = [{ x: 6, y: 6 }]
    setSnake(initialSnake)
    setFood(generateFood(initialSnake))
    setDirection(DIRECTIONS.RIGHT)
    directionRef.current = DIRECTIONS.RIGHT
    setGameOver(false)
    setScore(0)
    setIsPaused(false)
    setShowControls(false)
  }, [generateFood])

  // Game loop
  useEffect(() => {
    if (gameOver || isPaused || showControls) return

    gameLoopRef.current = setInterval(() => {
      setSnake(prevSnake => {
        const head = prevSnake[0]
        const newHead = {
          x: (head.x + directionRef.current.x + GRID_SIZE) % GRID_SIZE,
          y: (head.y + directionRef.current.y + GRID_SIZE) % GRID_SIZE,
        }

        // Check self collision
        if (prevSnake.some(seg => seg.x === newHead.x && seg.y === newHead.y)) {
          setGameOver(true)
          // Save high score
          if (score > highScore) {
            setHighScore(score)
            localStorage.setItem('emd_snake_highscore', score.toString())
          }
          return prevSnake
        }

        const newSnake = [newHead, ...prevSnake]

        // Check food collision
        if (newHead.x === food.x && newHead.y === food.y) {
          setScore(s => s + 1)
          setFood(generateFood(newSnake))
        } else {
          newSnake.pop()
        }

        return newSnake
      })
    }, INITIAL_SPEED - Math.min(score * 5, 80)) // Speed up with score

    return () => clearInterval(gameLoopRef.current)
  }, [gameOver, isPaused, food, generateFood, score, highScore, showControls])

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showControls) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          resetGame()
        }
        return
      }

      if (gameOver) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          resetGame()
        }
        return
      }

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'z':
          e.preventDefault()
          if (directionRef.current !== DIRECTIONS.DOWN) {
            directionRef.current = DIRECTIONS.UP
            setDirection(DIRECTIONS.UP)
          }
          break
        case 'ArrowDown':
        case 's':
          e.preventDefault()
          if (directionRef.current !== DIRECTIONS.UP) {
            directionRef.current = DIRECTIONS.DOWN
            setDirection(DIRECTIONS.DOWN)
          }
          break
        case 'ArrowLeft':
        case 'a':
        case 'q':
          e.preventDefault()
          if (directionRef.current !== DIRECTIONS.RIGHT) {
            directionRef.current = DIRECTIONS.LEFT
            setDirection(DIRECTIONS.LEFT)
          }
          break
        case 'ArrowRight':
        case 'd':
          e.preventDefault()
          if (directionRef.current !== DIRECTIONS.LEFT) {
            directionRef.current = DIRECTIONS.RIGHT
            setDirection(DIRECTIONS.RIGHT)
          }
          break
        case ' ':
          e.preventDefault()
          setIsPaused(p => !p)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [gameOver, resetGame, showControls])

  // Touch controls for mobile
  const touchStartRef = useRef({ x: 0, y: 0 })

  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    }
  }

  const handleTouchEnd = (e) => {
    if (showControls) {
      resetGame()
      return
    }

    if (gameOver) {
      resetGame()
      return
    }

    const dx = e.changedTouches[0].clientX - touchStartRef.current.x
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y

    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal swipe
      if (dx > 20 && directionRef.current !== DIRECTIONS.LEFT) {
        directionRef.current = DIRECTIONS.RIGHT
        setDirection(DIRECTIONS.RIGHT)
      } else if (dx < -20 && directionRef.current !== DIRECTIONS.RIGHT) {
        directionRef.current = DIRECTIONS.LEFT
        setDirection(DIRECTIONS.LEFT)
      }
    } else {
      // Vertical swipe
      if (dy > 20 && directionRef.current !== DIRECTIONS.UP) {
        directionRef.current = DIRECTIONS.DOWN
        setDirection(DIRECTIONS.DOWN)
      } else if (dy < -20 && directionRef.current !== DIRECTIONS.DOWN) {
        directionRef.current = DIRECTIONS.UP
        setDirection(DIRECTIONS.UP)
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center gap-2"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Score display */}
      <div className="flex items-center justify-between w-full px-1">
        <div className="flex items-center gap-1.5">
          <Gamepad2 className={`w-3 h-3 ${colors.text}`} />
          <span className="text-[10px] text-slate-400">Score: <span className="text-white font-bold">{score}</span></span>
        </div>
        {highScore > 0 && (
          <div className="flex items-center gap-1">
            <Trophy className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-amber-400">{highScore}</span>
          </div>
        )}
      </div>

      {/* Game grid */}
      <div
        className={`relative rounded-lg border ${colors.border} bg-slate-900/50 overflow-hidden`}
        style={{
          width: GRID_SIZE * CELL_SIZE,
          height: GRID_SIZE * CELL_SIZE,
        }}
      >
        {/* Grid background */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(to right, currentColor 1px, transparent 1px),
              linear-gradient(to bottom, currentColor 1px, transparent 1px)
            `,
            backgroundSize: `${CELL_SIZE}px ${CELL_SIZE}px`,
          }}
        />

        {/* Snake */}
        {snake.map((segment, index) => (
          <div
            key={index}
            className={`absolute rounded-sm transition-all duration-75 ${
              index === 0 ? colors.head : colors.body
            }`}
            style={{
              width: CELL_SIZE - 2,
              height: CELL_SIZE - 2,
              left: segment.x * CELL_SIZE + 1,
              top: segment.y * CELL_SIZE + 1,
              boxShadow: index === 0 ? '0 0 6px currentColor' : 'none',
            }}
          />
        ))}

        {/* Food */}
        <div
          className={`absolute ${colors.food} rounded-full animate-pulse`}
          style={{
            width: CELL_SIZE - 4,
            height: CELL_SIZE - 4,
            left: food.x * CELL_SIZE + 2,
            top: food.y * CELL_SIZE + 2,
            boxShadow: '0 0 8px currentColor',
          }}
        />

        {/* Start overlay */}
        {showControls && (
          <div className="absolute inset-0 bg-slate-900/90 flex flex-col items-center justify-center">
            <Gamepad2 className={`w-6 h-6 ${colors.text} mb-2`} />
            <p className="text-[10px] text-slate-400 text-center px-2">
              Fleches / ZQSD / Swipe
            </p>
            <button
              onClick={resetGame}
              className={`mt-2 px-3 py-1 text-[10px] ${colors.text} border ${colors.border} rounded hover:bg-slate-800 transition-colors`}
            >
              Jouer
            </button>
          </div>
        )}

        {/* Game over overlay */}
        {gameOver && (
          <div className="absolute inset-0 bg-slate-900/90 flex flex-col items-center justify-center">
            <p className="text-xs text-red-400 font-bold mb-1">Game Over!</p>
            <p className="text-[10px] text-slate-400 mb-2">Score: {score}</p>
            <button
              onClick={resetGame}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-slate-300 border border-slate-600 rounded hover:bg-slate-800 transition-colors"
            >
              <RotateCcw className="w-3 h-3" />
              Rejouer
            </button>
          </div>
        )}

        {/* Pause overlay */}
        {isPaused && !gameOver && !showControls && (
          <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center">
            <p className="text-xs text-slate-400">Pause (Espace)</p>
          </div>
        )}
      </div>

      {/* Mobile hint */}
      <p className="text-[9px] text-slate-600 text-center">
        Swipe ou fleches pour jouer
      </p>
    </div>
  )
}
